import React, { useState, useEffect, useRef, useContext } from 'react';
import PropTypes from 'prop-types';
import Grid from '@mui/material/Grid';
import SoftBox from 'components/SoftBox';
import SoftTypography from 'components/SoftTypography';
import FormField from 'layouts/applications/wizard/components/FormField';
import SoftButton from 'components/SoftButton';
import SoftSelect from 'components/SoftSelect';
import DataTable from 'prefabsComponents/Tables/DataTable';
import DashboardLayout from 'prefabsComponents/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'prefabsComponents/Navbars/DashboardNavbar';
import Footer from 'prefabsComponents/Footer';
import OrderCard from 'components/OrderCard';
import Card from '@mui/material/Card';
import IconButton from '@mui/material/IconButton';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Dropzone from 'dropzone';
import 'dropzone/dist/dropzone.css';
import Swal from 'sweetalert2';

import AccionesCell from './components/AccionesCell';
import ShipmentEditDocumentModal from './components/ShipmentEditDocumentModal';

import { AuthContext } from 'context/AuthContext';

// Toast
const Toast = Swal.mixin({
  toast: true,
  position: "top-end",
  showConfirmButton: false,
  timer: 3000,
  timerProgressBar: true,
  didOpen: (toast) => {
    toast.onmouseenter = Swal.stopTimer;
    toast.onmouseleave = Swal.resumeTimer;
  },
});

function OrderShipmentDocumentsList() {
  const { numeroOrdenCabecera, idOrdenDetalle } = useParams();
  const navigate = useNavigate();

  const [files, setFiles] = useState([]);
  const [dataTableData, setDataTableData] = useState({ columns: [], rows: [] });
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const dropzoneRef = useRef();
  const dropzoneInstance = useRef(null);
  const [orderInfo, setOrderInfo] = useState(null);

  // Roles
  const { hasRole } = useContext(AuthContext);
  const canUpload = hasRole('admin', 'user'); // admin/user pueden subir
  const isClient  = hasRole('client');        // cliente ve card + tabla lado a lado

  const documentOptions = [
    { value: 'recibo_bodega', label: 'Recibo bodega' },
    { value: 'embarque', label: 'Embarque' },
    { value: 'factura', label: 'Factura' },
    { value: 'foto', label: 'Foto' },
    { value: 'varios', label: 'Varios' },
  ];

  // Obtener info cabecera
  useEffect(() => {
    const fetchOrderInfo = async () => {
      try {
        const { data } = await axios.get(
          `${process.env.REACT_APP_SERVER_URL}/api/orders/orden-info/?id=${numeroOrdenCabecera}`
        );
        if (Array.isArray(data) && data.length > 0) {
          setOrderInfo(data[0]);
        }
      } catch (error) {
        console.error('Error fetching order info:', error);
      }
    };
    fetchOrderInfo();
  }, [numeroOrdenCabecera]);

  // Documentos del embarque
  const getData = async () => {
    try {
      const { data: documentos } = await axios.get(
        `${process.env.REACT_APP_SERVER_URL}/api/orders/shipments-documents/${idOrdenDetalle}/`
      );

      const columns = [
        { Header: 'ID Documento', accessor: 'id_documentos' },
        { Header: 'Nombre', accessor: 'nombre' },
        { Header: 'Nota', accessor: 'nota' },
        { Header: 'Tipo de Documento', accessor: 'tipo' },
        {
          Header: 'Acciones',
          accessor: 'acciones',
          Cell: ({ row }) => (
            <AccionesCell
              document={row.original}
              onEdit={() => handleEdit(row.original)}
              onDelete={() => handleDelete(row.original.id_documentos)}
            />
          ),
        },
      ];

      const rows = documentos.map(doc => ({
        id_documentos: doc.id_documentos,
        nombre: doc.nombre || 'Sin nombre',
        nota: doc.nota,
        tipo: doc.tipo,
        dir: doc.url,
      }));

      setDataTableData({ columns, rows });
    } catch (error) {
      console.error('Error fetching shipment document data:', error);
    }
  };

  useEffect(() => {
    getData();
  }, [idOrdenDetalle]);

  const handleEdit = (documento) => {
    setSelectedDocument(documento);
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setSelectedDocument(null);
  };

  const handleDelete = async (id_documentos) => {
    Swal.fire({
      title: '¿Está seguro?',
      text: 'Esta acción no se puede deshacer.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Sí, eliminar'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await axios.delete(`${process.env.REACT_APP_SERVER_URL}/api/orders/shipments-documents/${id_documentos}/`);
          setDataTableData(prev => ({
            ...prev,
            rows: prev.rows.filter(r => r.id_documentos !== id_documentos),
          }));
          Swal.fire({ icon: 'success', title: 'Eliminado', text: 'Documento eliminado correctamente' });
        } catch (error) {
          console.error('Error deleting shipment document:', error);
          Swal.fire({ icon: 'error', title: 'Error', text: 'Error al eliminar el documento' });
        }
      }
    });
  };

  // Dropzone: solo inicializar cuando se puede subir
  const handleFileAdded = (file) => {
    setFiles(prev => [
      ...prev,
      {
        id: file.upload.uuid,
        name: file.name,
        docName: '',
        note: '',
        type: '',
        raw: file,
      },
    ]);
  };

  const handleFileRemoved = (file) => {
    setFiles(prev => prev.filter(f => f.id !== file.upload.uuid));
  };

  useEffect(() => {
    if (!canUpload) {
      // destruir si existía
      if (dropzoneInstance.current) {
        dropzoneInstance.current.destroy();
        dropzoneInstance.current = null;
      }
      return;
    }

    if (dropzoneRef.current) {
      const dz = new Dropzone(dropzoneRef.current, {
        url: '/file-upload',
        addRemoveLinks: true,
        autoProcessQueue: false,
        dictDefaultMessage: 'Arrastra los archivos aquí o haz clic para seleccionarlos',
        dictRemoveFile: 'Quitar archivo',
        dictCancelUpload: 'Cancelar',
        dictFileTooBig: 'El archivo es demasiado grande ({{filesize}}MB). Máximo: {{maxFilesize}}MB.',
        dictInvalidFileType: 'No puedes subir archivos de este tipo.',
      });
      dropzoneInstance.current = dz;

      dz.on('addedfile', handleFileAdded);
      dz.on('removedfile', handleFileRemoved);

      return () => {
        dz.destroy();
        dropzoneInstance.current = null;
      };
    }
  }, [canUpload]);

  const handleInputChange = (id, field, value) => {
    setFiles(prev => prev.map(file => (file.id === id ? { ...file, [field]: value } : file)));
  };

  const handleUploadDocuments = async () => {
    if (!canUpload) return; // seguridad

    if (files.length === 0) {
      Toast.fire({ icon: "warning", title: "No hay archivos para cargar" });
      return;
    }

    for (let i = 0; i < files.length; i++) {
      const { name, docName, note, type } = files[i];
      if (!docName || !note || !type) {
        Toast.fire({ icon: "warning", title: `Faltan campos en el archivo "${name}"` });
        return;
      }
    }

    try {
      const formData = new FormData();
      const documentosMeta = files.map((file, index) => ({
        index,
        nombreArchivo: file.name,
        nombreDocumento: file.docName,
        nota: file.note,
        tipo: file.type,
      }));

      formData.append("meta", JSON.stringify(documentosMeta));
      files.forEach((file, index) => formData.append(`archivo_${index}`, file.raw));
      formData.append("numeroOrden", idOrdenDetalle);

      await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/api/orders/shipments-documents/${idOrdenDetalle}/`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      Swal.fire({ icon: "success", title: "¡Documentos cargados con éxito!" });
      if (dropzoneInstance.current) dropzoneInstance.current.removeAllFiles();
      setFiles([]);
      getData();
    } catch (error) {
      console.error("Error al cargar documentos:", error);
      Swal.fire({ icon: "error", title: "Ocurrió un error al cargar los documentos" });
    }
  };

  const orderCardInfo = orderInfo
    ? {
        cliente: orderInfo.cliente,
        'Fecha de Embarque': orderInfo.fecha,
        'Fecha de Arribo': orderInfo.entrega,
        'Fecha Real de Arribo': orderInfo.real_entrega,
        responsable: orderInfo.responsable,
        proveedor: orderInfo.proveedor,
        incoterms: orderInfo.incoterms,
        transporte: orderInfo.transporte,
        origen: orderInfo.origen,
        status: orderInfo.status,
      }
    : {};

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <SoftBox mb={2} display="flex" flexDirection="column" alignItems="flex-end">
        <IconButton
          aria-label="back"
          onClick={() => navigate(-1)}
          style={{ padding: '4px', fontSize: '1.275rem' }}
        >
          <ArrowBackIosNewIcon fontSize="small" style={{ marginRight: '4px' }} />
          Regresar
        </IconButton>
      </SoftBox>

      <SoftBox my={3}>
        {/* ======= Fila superior ======= */}
        <SoftBox display="flex" flexDirection="row" gap={2} alignItems="flex-start">
          <OrderCard numero={orderInfo ? `#${orderInfo.numero}` : ''} info={orderCardInfo} />

          {/* Cliente: card + tabla lado a lado */}
          {isClient ? (
            <Card style={{ flexGrow: 1, padding: 20 }}>
              <DataTable
                table={dataTableData}
                entriesPerPage={false}
                canSearch
                title={`Documentos de Embarque #${idOrdenDetalle}`}
              />
            </Card>
          ) : (
            // Admin/User: card + dropzone (tabla va abajo)
            canUpload && (
              <Card style={{ flexGrow: 1, padding: 20 }}>
                <SoftBox
                  ref={dropzoneRef}
                  className="dropzone"
                  mt={2}
                  style={{
                    padding: '20px',
                    border: '2px dashed #ddd',
                    borderRadius: '8px',
                  }}
                />

                <SoftBox mt={4}>
                  {files.map((file) => (
                    <Grid container spacing={3} key={file.id} sx={{ mb: 2 }}>
                      <Grid item xs={12} md={3}>
                        <FormField type="text" label="Nombre del archivo" value={file.name} disabled />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <FormField
                          type="text"
                          label="Nombre documento"
                          placeholder="Nombre del documento"
                          value={file.docName}
                          onChange={(e) => handleInputChange(file.id, 'docName', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <FormField
                          type="text"
                          label="Nota documento"
                          placeholder="Añadir nota"
                          value={file.note}
                          onChange={(e) => handleInputChange(file.id, 'note', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <SoftTypography variant="caption" fontWeight="bold" color="text">
                          Tipo de documento
                        </SoftTypography>
                        <SoftSelect
                          options={documentOptions}
                          placeholder="Seleccione el tipo de documento"
                          value={documentOptions.find((option) => option.value === file.type)}
                          onChange={(selectedOption) =>
                            handleInputChange(file.id, 'type', selectedOption.value)
                          }
                          menuPortalTarget={document.body}
                          styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                        />
                      </Grid>
                    </Grid>
                  ))}
                </SoftBox>

                <SoftBox mt={2} display="flex" justifyContent="center" width="100%">
                  <SoftButton variant="gradient" color="info" onClick={handleUploadDocuments}>
                    Cargar Documentos
                  </SoftButton>
                </SoftBox>
              </Card>
            )
          )}
        </SoftBox>

        {/* ======= Tabla abajo solo para admin/user ======= */}
        {!isClient && (
          <SoftBox mt={4}>
            <Card>
              <DataTable
                table={dataTableData}
                entriesPerPage={false}
                canSearch
                title={`Documentos de Embarque #${idOrdenDetalle}`}
              />
            </Card>
          </SoftBox>
        )}
      </SoftBox>

      <Footer />

      {/* Modal editar documento */}
      <ShipmentEditDocumentModal
        open={showEditModal}
        handleClose={handleCloseEditModal}
        initialData={selectedDocument}
        onRefresh={getData}
      />
    </DashboardLayout>
  );
}

OrderShipmentDocumentsList.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      id_documentos: PropTypes.number.isRequired,
    }).isRequired,
  }),
};

export default OrderShipmentDocumentsList;
